import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Navigation, Truck, CheckCircle, AlertCircle, Loader2, Share2, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://i.ibb.co/7xkFLsM4/tempus-logo-white-blue.png";

export default function DriverTrackingPage() {
  const { token } = useParams();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, watching, error
  const [watchId, setWatchId] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch session data
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/tracking/driver-page/${token}`);
        if (!response.ok) throw new Error('Session not found');
        const data = await response.json();
        setSessionData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [token]);

  // Send location update to server
  const sendLocationUpdate = useCallback(async (position) => {
    try {
      const response = await fetch(`${API_URL}/api/tracking/update/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed ? position.coords.speed * 2.237 : null, // m/s to mph
          heading: position.coords.heading
        })
      });
      
      if (response.ok) {
        setLastUpdate(new Date());
        const data = await response.json();
        // Check for alerts
        if (data.alerts && data.alerts.length > 0) {
          data.alerts.forEach(alert => {
            // Could show a notification here
            console.log('Geofence alert:', alert.message);
          });
        }
      }
    } catch (err) {
      console.error('Failed to send location:', err);
    }
  }, [token]);

  // Start location sharing
  const startSharing = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setSharing(true);
    setLocationStatus('watching');

    const id = navigator.geolocation.watchPosition(
      (position) => {
        sendLocationUpdate(position);
      },
      (err) => {
        setLocationStatus('error');
        setError(`Location error: ${err.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000
      }
    );

    setWatchId(id);
  };

  // Stop location sharing
  const stopSharing = () => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setSharing(false);
    setLocationStatus('idle');
  };

  // Copy tracking link
  const copyTrackingLink = () => {
    const link = `https://tempusdispatch.com/track/${token}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Session Not Found</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-3">
            <img src={LOGO_URL} alt="Tempus Dispatch" className="h-8" />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Status Card */}
        <div className={`rounded-xl p-6 mb-6 ${
          sharing 
            ? 'bg-green-500/10 border border-green-500/30' 
            : 'bg-slate-800/50 border border-slate-700/50'
        }`}>
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              sharing ? 'bg-green-600' : 'bg-slate-700'
            }`}>
              {sharing ? (
                <Navigation className="w-8 h-8 text-white animate-pulse" />
              ) : (
                <MapPin className="w-8 h-8 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {sharing ? '📍 Sharing Location' : 'Share Your Location'}
              </h1>
              <p className="text-gray-400">
                {sharing 
                  ? 'Your location is being shared with the shipper'
                  : 'Tap the button below to start sharing'
                }
              </p>
            </div>
          </div>

          {/* Start/Stop Button */}
          <button
            onClick={sharing ? stopSharing : startSharing}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              sharing
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {sharing ? '⏹️ Stop Sharing' : '▶️ Start Sharing Location'}
          </button>

          {/* Last Update */}
          {sharing && lastUpdate && (
            <p className="text-center text-green-400 text-sm mt-3">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              Last update: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Load Details */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-400" />
            Load Details
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-400">PICKUP</span>
                </div>
                <p className="text-white font-medium">
                  {sessionData?.origin?.city || 'N/A'}, {sessionData?.origin?.state || ''}
                </p>
                {sessionData?.pickup_date && (
                  <p className="text-sm text-gray-400">{sessionData.pickup_date}</p>
                )}
              </div>
            </div>

            <div className="border-l-2 border-dashed border-slate-600 ml-1.5 h-6"></div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-gray-400">DELIVERY</span>
                </div>
                <p className="text-white font-medium">
                  {sessionData?.destination?.city || 'N/A'}, {sessionData?.destination?.state || ''}
                </p>
                {sessionData?.delivery_date && (
                  <p className="text-sm text-gray-400">{sessionData.delivery_date}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Share Link */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-400" />
            Shipper Tracking Link
          </h3>
          
          <p className="text-gray-400 text-sm mb-4">
            Share this link with the shipper so they can track your progress:
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={`tempusdispatch.com/track/${token}`}
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm"
            />
            <button
              onClick={copyTrackingLink}
              className={`px-4 py-3 rounded-lg font-medium transition-all ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {copied ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
          
          {copied && (
            <p className="text-green-400 text-sm mt-2">✓ Link copied to clipboard!</p>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <h4 className="font-semibold text-blue-400 mb-2">📱 How it works:</h4>
          <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
            <li>Tap "Start Sharing Location" above</li>
            <li>Allow location access when prompted</li>
            <li>Keep this page open while driving</li>
            <li>Your position updates automatically every few seconds</li>
            <li>Tap "Stop Sharing" when you've delivered</li>
          </ol>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-500 text-sm">
        <p>Powered by <span className="text-blue-400">Tempus Dispatch</span></p>
      </footer>
    </div>
  );
}
